import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { FileText, Activity, CheckCircle2, AlertTriangle, Plus, ArrowRight } from "lucide-react";

const statusColor = {
  draft: "bg-slate-100 text-slate-700 border-slate-200",
  in_progress: "bg-yellow-100 text-yellow-900 border-yellow-200",
  completed: "bg-emerald-100 text-emerald-900 border-emerald-200",
  closed: "bg-blue-100 text-blue-900 border-blue-200",
};

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [s, r] = await Promise.all([
          api.get("/stats"),
          api.get("/job-cards"),
        ]);
        setStats(s.data);
        setRecent(Array.isArray(r.data) ? r.data.slice(0, 6) : []);
      } catch (e) {}
    })();
  }, []);

  const cards = [
    { label: "Total Job Cards", value: stats?.total ?? 0, icon: FileText, hint: "All time" },
    { label: "In Progress", value: stats?.in_progress ?? 0, icon: Activity, hint: "Active on floor", accent: true },
    { label: "Completed", value: stats?.completed ?? 0, icon: CheckCircle2, hint: "Ready to close" },
    { label: "Wastage Units", value: stats?.wastage_qty ?? 0, icon: AlertTriangle, hint: "Cumulative" },
  ];

  return (
    <Layout>
      <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-yellow-600 font-semibold mb-1">Production Overview</div>
          <h1 className="text-3xl sm:text-4xl tracking-tight font-semibold text-slate-900" style={{ fontFamily: "var(--font-heading)" }}>
            Dashboard
          </h1>
          <p className="text-slate-500 mt-2 text-sm">Monitor active jobs, output, and yield across the printing line.</p>
        </div>
        <Link to="/job-cards/new" data-testid="dashboard-new-jc">
          <Button className="bg-yellow-400 hover:bg-yellow-500 text-slate-900 font-medium">
            <Plus className="w-4 h-4 mr-2" /> New Job Card
          </Button>
        </Link>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {cards.map((c) => (
          <Card key={c.label} className={`p-5 border ${c.accent ? "border-yellow-200 bg-yellow-50/40" : "border-slate-200 bg-white"}`}>
            <div className="flex items-center justify-between">
              <div className="text-[11px] uppercase tracking-widest text-slate-500 font-medium">{c.label}</div>
              <c.icon className={`w-4 h-4 ${c.accent ? "text-yellow-600" : "text-slate-400"}`} />
            </div>
            <div className="mt-3 text-3xl font-semibold tracking-tight text-slate-900" data-testid={`stat-${c.label.toLowerCase().replace(/\s+/g, "-")}`}>
              {c.value}
            </div>
            <div className="text-xs text-slate-500 mt-1">{c.hint}</div>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8">
        <Card className="p-5 border-slate-200">
          <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-2">Ordered Qty</div>
          <div className="text-2xl font-mono">{stats?.ordered_qty ?? 0}</div>
        </Card>
        <Card className="p-5 border-slate-200">
          <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-2">Good Qty</div>
          <div className="text-2xl font-mono text-emerald-700">{stats?.good_qty ?? 0}</div>
        </Card>
        <Card className="p-5 border-slate-200">
          <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-2">Rejected Qty</div>
          <div className="text-2xl font-mono text-red-700">{stats?.rejected_qty ?? 0}</div>
        </Card>
      </div>

      <Card className="border-slate-200 overflow-hidden">
        <div className="flex items-center justify-between p-5 border-b border-slate-200">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-slate-500">Recent</div>
            <div className="text-lg font-semibold" style={{ fontFamily: "var(--font-heading)" }}>Latest Job Cards</div>
          </div>
          <Link to="/job-cards" className="text-sm text-slate-600 hover:text-slate-900 flex items-center gap-1" data-testid="view-all-jc">
            View all <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-widest text-slate-500 bg-slate-50 border-b border-slate-200">
                <th className="px-5 py-3 font-medium">Job Card No.</th>
                <th className="px-5 py-3 font-medium">Customer</th>
                <th className="px-5 py-3 font-medium">PO</th>
                <th className="px-5 py-3 font-medium">Item</th>
                <th className="px-5 py-3 font-medium text-right">Ordered</th>
                <th className="px-5 py-3 font-medium">Status</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {recent.length === 0 && (
                <tr>
                  <td colSpan={7} className="p-10 text-center text-slate-500">
                    No job cards yet. <Link to="/job-cards/new" className="text-yellow-600 font-medium hover:underline">Create the first one</Link>.
                  </td>
                </tr>
              )}
              {recent.map((j) => (
                <tr key={j.id} className="border-b border-slate-100 hover:bg-slate-50/70">
                  <td className="px-5 py-3 font-mono text-slate-900">{j.job_card_no}</td>
                  <td className="px-5 py-3">{j.customer_name}</td>
                  <td className="px-5 py-3 text-slate-600">{j.customer_po}</td>
                  <td className="px-5 py-3 text-slate-600">{(j.items && j.items[0]?.item_name) || j.item_details?.item_name || "—"}</td>
                  <td className="px-5 py-3 font-mono text-right">{j.ordered_qty}</td>
                  <td className="px-5 py-3">
                    <Badge className={`${statusColor[j.status] || ""} border text-[10px] uppercase tracking-wider font-medium`}>{(j.status || "").replace("_", " ")}</Badge>
                  </td>
                  <td className="px-5 py-3 text-right">
                    <Link to={`/job-cards/${j.id}`} className="text-sm text-slate-700 hover:text-slate-900 font-medium">Open</Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </Layout>
  );
}
